import React, { useState } from 'react';
import UISectionWrapper, { UISectionHeader, UISectionBody } from '@/components/shared/UISectionWrapper';
import UISectionTitle from '@/components/shared/UISectionTitle';
import UIIconExtraButton from '@/components/shared/UIIconExtraButton';
import './UICollapsibleSection.scss';

export interface UICollapsibleSectionProps {
  /**
   * Заголовок секції
   */
  title: string;

  /**
   * Опис секції (опціонально)
   */
  description?: string;

  /**
   * Вміст секції
   */
  children: React.ReactNode;

  /**
   * Початковий стан згорнутості
   */
  defaultCollapsed?: boolean;

  /**
   * Контрольований стан згорнутості
   */
  collapsed?: boolean;

  /**
   * Обробник зміни стану згорнутості
   */
  onToggleCollapse?: (collapsed: boolean) => void;

  /**
   * Додаткові дії в заголовку (поруч з кнопкою згортання)
   */
  headerActions?: React.ReactNode;

  /**
   * Розмір заголовка
   */
  titleSize?: 'small' | 'medium' | 'large';

  /**
   * Варіант кольору заголовка
   */
  titleVariant?: 'default' | 'primary' | 'secondary';

  /**
   * Стан завантаження
   */
  loading?: boolean;

  /**
   * Текст завантаження
   */
  loadingText?: string;

  /**
   * Можливість згортання (якщо false, кнопка згортання не показується)
   */
  collapsible?: boolean;

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Props для UISectionWrapper
   */
  wrapperProps?: Record<string, any>;
}

const baseClass = 'UICollapsibleSection';

const UICollapsibleSection: React.FC<UICollapsibleSectionProps> = ({
                                                                     title,
                                                                     description,
                                                                     children,
                                                                     defaultCollapsed = false,
                                                                     collapsed,
                                                                     onToggleCollapse,
                                                                     headerActions,
                                                                     titleSize = 'medium',
                                                                     titleVariant = 'default',
                                                                     loading = false,
                                                                     loadingText = 'Loading...',
                                                                     collapsible = true,
                                                                     className = '',
                                                                     wrapperProps = {},
                                                                   }) => {
  // Внутрішній стан згорнутості
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);

  // Використовуємо контрольований або неконтрольований стан
  const isCollapsed = collapsed !== undefined ? collapsed : internalCollapsed;

  const handleToggleCollapse = () => {
    const newCollapsed = !isCollapsed;

    // Оновлюємо внутрішній стан якщо не контрольований
    if (collapsed === undefined) {
      setInternalCollapsed(newCollapsed);
    }

    // Викликаємо callback
    onToggleCollapse?.(newCollapsed);
  };

  const renderHeaderActions = () => {
    const actions = [];

    // Додаємо додаткові дії
    if (headerActions) {
      actions.push(
        <div key="header-actions" className={`${baseClass}__header-actions`}>
          {headerActions}
        </div>
      );
    }

    // Додаємо кнопку згортання
    if (collapsible) {
      actions.push(
        <UIIconExtraButton
          key="collapse-btn"
          icon={isCollapsed ? 'expand' : 'collapse'}
          onClick={handleToggleCollapse}
          ariaLabel={isCollapsed ? 'Expand section' : 'Collapse section'}
          className={`${baseClass}__collapse-btn`}
        />
      );
    }

    return actions.length > 0 ? (
      <div className={`${baseClass}__actions`}>
        {actions}
      </div>
    ) : undefined;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className={`${baseClass}__loading`}>
          {loadingText}
        </div>
      );
    }

    return children;
  };

  const sectionClasses = [
    baseClass,
    isCollapsed && `${baseClass}--collapsed`,
    loading && `${baseClass}--loading`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <UISectionWrapper
      spacing="default"
      align="stretch"
      className={sectionClasses}
      {...wrapperProps}
    >
      <UISectionHeader>
        <UISectionTitle
          title={title}
          description={description}
          action={renderHeaderActions()}
          size={titleSize}
          variant={titleVariant}
          showDivider={true}
        />
      </UISectionHeader>

      {!isCollapsed && (
        <UISectionBody>
          {renderContent()}
        </UISectionBody>
      )}
    </UISectionWrapper>
  );
};

export default UICollapsibleSection;